import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Alert,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import testService from '../../services/testService';

// Register the required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * QuarterlyTestGrowthChart component for displaying quarterly test case growth trends
 * Shows a single bar chart with test case growth per quarter from the selected year's Q1 to today
 */
const QuarterlyTestGrowthChart = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quarterlyData, setQuarterlyData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(2023);
  
  // Generate year options from 2018 to current year
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2018 + 1 }, (_, i) => 2018 + i);

  // Define simplified automation status categories
  const automationCategories = [
    'Automated',
    'Manual',
    'Requires Automation'
  ];

  // Define colors for simplified categories
  const statusColors = {
    'Automated': '#00B050',           // Green
    'Manual': '#CD5C5C',              // Indian Red
    'Requires Automation': '#2F4F4F'  // Dark Slate Gray
  };

  // Fetch quarterly data from API
  const fetchQuarterlyData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await testService.getQuarterlyGrowthData({
        startYear: selectedYear,
        endYear: new Date().getFullYear()
      });
      
      if (response.success) {
        setQuarterlyData(response.data);
      } else {
        setError('Failed to fetch quarterly data');
      }
    } catch (err) {
      console.error('Error fetching quarterly data:', err);
      setError(err.message || 'Failed to fetch quarterly data');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when selected year changes
  useEffect(() => {
    fetchQuarterlyData();
  }, [selectedYear]);
  
  // Handle year selection change
  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  // Create consolidated chart data for all quarters
  const createConsolidatedChartData = () => {
    if (!quarterlyData || quarterlyData.length === 0) return null;

    // Create labels for each quarter
    const labels = quarterlyData.map(quarter => quarter.quarterName);
    
    // Calculate total tests created per quarter by simplified automation status
    const datasets = automationCategories.map(category => {
      const data = quarterlyData.map(quarter => {
        // Sum up all tests of this automation status across all months in the quarter
        return quarter.months.reduce((sum, month) => {
          let categorySum = 0;
          
          if (category === 'Automated') {
            // Group: Automated-UI + Automated-API + Automated in New Framework
            categorySum = (month['Automated-UI'] || 0) + 
                         (month['Automated-API'] || 0) + 
                         (month['Automated in New Framework'] || 0);
          } else if (category === 'Manual') {
            // Group: Manual + TBD + Not Automated
            categorySum = (month['Manual'] || 0) + 
                         (month['TBD'] || 0) + 
                         (month['Not Automated'] || 0);
          } else if (category === 'Requires Automation') {
            // Keep as is
            categorySum = (month['Requires Automation'] || 0);
          }
          
          return sum + categorySum;
        }, 0);
      });

      return {
        label: category,
        data: data,
        backgroundColor: statusColors[category] + '80', // Add transparency
        borderColor: statusColors[category],
        borderWidth: 1,
        stack: 'quarterly'
      };
    });

    // Add cumulative total line
    const cumulativeData = quarterlyData.map(quarter => {
      // Get the cumulative total at the end of each quarter
      const lastMonth = quarter.months[quarter.months.length - 1];
      return lastMonth ? lastMonth.cumulativeTotal : 0;
    });

    const lineDataset = {
      type: 'line',
      label: 'Cumulative Total',
      data: cumulativeData,
      borderColor: '#FF4500',
      backgroundColor: 'rgba(255, 69, 0, 0.1)',
      borderWidth: 3,
      fill: false,
      tension: 0.4,
      pointBackgroundColor: '#FF4500',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8,
      yAxisID: 'y1',
    };

    return {
      labels,
      datasets: [...datasets, lineDataset]
    };
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: `Quarterly Test Case Growth (${selectedYear} Q1 - Present)\nTest Cases Created per Quarter vs Cumulative Total`,
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 12,
          padding: 10,
          usePointStyle: true,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#2196f3',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const label = context.dataset.label;
            const value = context.raw;
            if (label === 'Cumulative Total') {
              return `${label}: ${value.toLocaleString()} total test cases`;
            } else {
              return `${label}: ${value} test cases`;
            }
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Quarter',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Test Cases Created per Quarter',
          font: {
            size: 12,
            weight: 'bold'
          },
          color: '#2196f3'
        },
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: function(value) {
            return Number.isInteger(value) ? value : '';
          },
          color: '#2196f3'
        },
        grid: {
          drawOnChartArea: false
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Cumulative Total',
          font: {
            size: 12,
            weight: 'bold'
          },
          color: '#FF4500'
        },
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: function(value) {
            return Number.isInteger(value) ? value.toLocaleString() : '';
          },
          color: '#FF4500'
        },
        grid: {
          drawOnChartArea: true
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  const chartData = createConsolidatedChartData();

  // Calculate summary statistics
  const totalTestsCreated = quarterlyData.reduce((sum, quarter) => {
    return sum + quarter.months.reduce((qSum, month) => qSum + month.totalCount, 0);
  }, 0);

  const finalCumulativeTotal = quarterlyData.length > 0 ? 
    quarterlyData[quarterlyData.length - 1]?.months[quarterlyData[quarterlyData.length - 1]?.months.length - 1]?.cumulativeTotal || 0 : 0;

  const totalAutomatedTests = quarterlyData.reduce((sum, quarter) => {
    return sum + quarter.months.reduce((qSum, month) => {
      return qSum + (month['Automated in New Framework'] || 0) + 
                   (month['Automated-API'] || 0) + 
                   (month['Automated-UI'] || 0);
    }, 0);
  }, 0);

  const automationRate = totalTestsCreated > 0 ? Math.round((totalAutomatedTests / totalTestsCreated) * 100) : 0;

  // Show loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading quarterly test data...
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Quarterly Test Case Growth ({selectedYear} Q1 - Present)
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
          Test case creation trends by quarter with automation status breakdown and cumulative growth.
        </Typography>
        
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="year-select-label">Start Year</InputLabel>
          <Select
            labelId="year-select-label"
            id="year-select"
            value={selectedYear}
            label="Start Year"
            onChange={handleYearChange}
          >
            {yearOptions.map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {quarterlyData.length === 0 ? (
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="center" 
          height="400px"
        >
          <Typography variant="h6" color="textSecondary">
            No quarterly test data available
          </Typography>
        </Box>
      ) : (
        <>
          <Box height="500px" width="100%" mb={3}>
            <Chart type="bar" data={chartData} options={chartOptions} />
          </Box>
          
          <Box>
            <Typography variant="h6" gutterBottom>
              Summary Statistics
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Chip 
                label={`Total Quarters: ${quarterlyData.length}`}
                color="info" 
              />
              <Chip 
                label={`Tests Created: ${totalTestsCreated.toLocaleString()}`}
                color="primary" 
              />
              <Chip 
                label={`Current Total: ${finalCumulativeTotal.toLocaleString()}`}
                color="secondary" 
              />
              <Chip 
                label={`Automation Rate: ${automationRate}%`}
                color="success" 
              />
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default QuarterlyTestGrowthChart;
